import { Link, router } from '@inertiajs/react'
import { AdminShell } from '@/components/layout/AdminShell'
import { PageHeader } from '@/components/landing/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface AdminUser {
  id: string
  email: string
  role: string
  suspended: boolean
  suspended_at: string | null
  created_at: string
  org_name: string | null
}

interface Props {
  admin: { email: string }
  user: AdminUser
}

export default function Show({ admin, user }: Props) {
  const isSuperAdmin = user.role === 'super_admin'

  function suspend() {
    router.post(`/admin/users/${user.id}/suspend`)
  }

  function unsuspend() {
    router.post(`/admin/users/${user.id}/unsuspend`)
  }

  function impersonate() {
    router.post('/admin/impersonations', { user_id: user.id })
  }

  function destroy() {
    if (confirm(`Delete user "${user.email}"? This cannot be undone.`)) {
      router.delete(`/admin/users/${user.id}`)
    }
  }

  const detailRows = [
    { label: 'Role', value: <Badge variant={isSuperAdmin ? 'destructive' : 'secondary'}>{user.role}</Badge> },
    {
      label: 'Status',
      value: (
        <Badge variant={user.suspended ? 'destructive' : 'default'}>
          {user.suspended ? 'suspended' : 'active'}
        </Badge>
      ),
    },
    { label: 'Organization', value: <span>{user.org_name ?? '—'}</span> },
    { label: 'Joined', value: <span>{new Date(user.created_at).toLocaleDateString()}</span> },
    ...(user.suspended_at
      ? [{ label: 'Suspended at', value: <span>{new Date(user.suspended_at).toLocaleDateString()}</span> }]
      : []),
  ]

  return (
    <AdminShell admin={admin}>
      <div className="space-y-8">
        <Link
          href="/admin/users"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          ← Users
        </Link>

        <PageHeader
          eyebrow="User"
          title={user.email}
          actions={
            !isSuperAdmin && (
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={impersonate}>Impersonate</Button>
                {user.suspended ? (
                  <Button variant="outline" onClick={unsuspend}>Unsuspend</Button>
                ) : (
                  <Button variant="outline" onClick={suspend}>Suspend</Button>
                )}
                <Button variant="destructive" onClick={destroy}>Delete</Button>
              </div>
            )
          }
        />

        <section className="surface-card p-6">
          <h2 className="font-heading text-base font-semibold tracking-tight">
            Details
          </h2>
          <dl className="mt-4 divide-y divide-foreground/[0.06] text-sm">
            {detailRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between py-3">
                <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  {row.label}
                </dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </AdminShell>
  )
}
